import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map(ch => ch.charCodeAt(0)));
}

export function usePushNotifications(token) {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [supported] = useState(
    typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window
  );

  const subscribe = useCallback(async () => {
    if (!supported || !token) return false;
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return false;

      const registration = await navigator.serviceWorker.register('/push-sw.js');
      await navigator.serviceWorker.ready;

      // Get VAPID key from backend
      const { data } = await axios.get(`${API}/push/vapid-key`);
      if (!data.publicKey) return false;

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });

      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, subscription.toJSON(), {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSubscribed(true);
      return true;
    } catch (e) {
      console.error('Push subscribe error:', e);
      return false;
    }
  }, [supported, token]);

  const unsubscribe = useCallback(async () => {
    if (!supported || !token) return;
    try {
      const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await axios.post(`${API}/push/unsubscribe`, { endpoint: subscription.endpoint }, {
            headers: { Authorization: `Bearer ${token}` },
          });
          await subscription.unsubscribe();
        }
      }
      setSubscribed(false);
    } catch (e) {
      console.error('Push unsubscribe error:', e);
    }
  }, [supported, token]);

  // Check existing subscription on mount
  useEffect(() => {
    if (!supported || !token) return;
    (async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
        if (registration) {
          const sub = await registration.pushManager.getSubscription();
          setSubscribed(!!sub);
        }
      } catch {}
    })();
  }, [supported, token]);

  return { permission, subscribed, supported, subscribe, unsubscribe };
}
